/**
 * Feuille de brouillon dans le player live (défi privé, migration 0651).
 *
 * Une question « avec feuille » arrive avec sa feuille publiée (livrée par
 * `live-key` en même temps que la clé : jamais avant l'heure). Une feuille par
 * question, gardée pendant tout le défi ; les lignes validées partent en tâche
 * de fond vers `record_live_scratch_line` (le serveur plafonne les HORA à la
 * feuille et refuse après la réponse). Les HORA de brouillon sont versés à la
 * SKILLCARD de la question au moment de la réponse (`submit-live-answer`).
 *
 * Feuille du moteur v2 (`track = 'packs'`) : jouée avec l'interface du prototype,
 * comme dans le player d'exercice. Le moteur et l'interface se chargent à la
 * demande, à la première question qui a une feuille v2 ; s'ils ne se chargent
 * pas, la question garde sa version du noyau (ancienne interface).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { ScratchSession } from '@/features/scratch-sheet/scratch-session';
import {
  createLineRecorder,
  erasePayload,
  linePayload,
  loadV2Ui,
  parseScratchPayload,
  type EraseLinePayload,
  type ScratchLinePayload,
  type V2Ui,
} from '@/features/scratch-sheet/scratch-data';
import { loadPacksModule, type PacksModule } from '@/features/scratch-sheet/sheet-engine';
import { withChoiceValues } from '@/features/scratch-sheet/choice-values';
import type { V2Session } from '@/features/brouillon-v2';
import type { DecryptedStep } from './use-live-quiz';

type UntypedRpc = (
  name: string,
  params: Record<string, unknown>,
) => Promise<{ data: unknown; error: { message?: string } | null }>;
const rpc: UntypedRpc = (name, params) =>
  (supabase as unknown as { rpc: UntypedRpc }).rpc(name, params);

/** Feuille fermée côté serveur (question finie ou répondue) : inutile de réessayer. */
export function isClosedLiveScratchError(message: string | undefined): boolean {
  return /scratch_live_(closed|answered|not_joined|not_found)/.test(message ?? '');
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * Paramètres de `record_live_scratch_line` depuis la charge commune de la
 * feuille (le « numéro de tentative » porte ici l'index de la question). PUR.
 */
export function liveLineParams(
  challengeId: string,
  p: ScratchLinePayload,
): Record<string, unknown> {
  return {
    p_challenge_id: challengeId,
    p_step_index: Number(p.p_attempt_id),
    p_atoms: p.p_atoms,
    p_line_text: p.p_line_text,
    p_kind: p.p_kind,
    p_rule_id: p.p_rule_id,
    p_hora: p.p_hora,
    p_hinted: p.p_hinted,
    p_track: p.p_track,
  };
}

/** Feuille v2 d'une question du live : sa session et l'interface qui la dessine. */
export interface LiveV2Sheet {
  session: V2Session;
  ui: V2Ui;
}

type V2Modules = { packs: PacksModule; ui: V2Ui } | 'loading' | 'failed' | null;

/** La feuille livrée a une version v2 (`packs`). PUR. */
export function hasPacksSheet(sheet: { models?: unknown } | null | undefined): boolean {
  return (
    Array.isArray(sheet?.models) &&
    (sheet.models as { track?: unknown }[]).some((m) => m?.track === 'packs')
  );
}

export function useLiveScratch(challengeId: string | null) {
  const sessions = useRef(new Map<number, ScratchSession>());
  const v2Sessions = useRef(new Map<number, V2Session>());
  const broken = useRef(new Set<number>());
  // moteur v2 + interface : chargés une fois, à la première feuille v2
  const [mods, setMods] = useState<V2Modules>(null);
  const requested = useRef(false);
  // appelé pendant le rendu (`get`) : le chargement part hors rendu
  const requestV2 = useCallback(() => {
    if (requested.current) return;
    requested.current = true;
    queueMicrotask(() => {
      setMods('loading');
      Promise.all([loadPacksModule(), loadV2Ui()])
        .then(([packs, ui]) => setMods({ packs, ui }))
        .catch((e) => {
          console.warn('[live-scratch] moteur v2 indisponible : ancien moteur', e);
          setMods('failed');
        });
    });
  }, []);

  const recorder = useMemo(() => {
    const send = async (p: ScratchLinePayload) => {
      if (!challengeId) return;
      const { error } = await rpc('record_live_scratch_line', liveLineParams(challengeId, p));
      if (error && !isClosedLiveScratchError(error.message)) throw error;
    };
    const erase = async (p: EraseLinePayload) => {
      if (!challengeId) return;
      const { error } = await rpc('delete_last_live_scratch_line', {
        p_challenge_id: challengeId,
        p_step_index: Number(p.p_attempt_id),
        p_line_text: p.p_line_text,
      });
      if (error && !isClosedLiveScratchError(error.message)) throw error;
    };
    return createLineRecorder(send, { erase });
  }, [challengeId]);

  // Nouveau défi : nouvelles feuilles.
  const genRef = useRef(challengeId);
  if (genRef.current !== challengeId) {
    sessions.current.forEach((s) => s.dispose());
    sessions.current = new Map();
    v2Sessions.current = new Map();
    broken.current = new Set();
    genRef.current = challengeId;
  }

  useEffect(() => {
    const wake = () => recorder.wake();
    window.addEventListener('online', wake);
    return () => window.removeEventListener('online', wake);
  }, [recorder]);

  /** Feuille de la question `stepIndex` (null si la question se joue sans feuille). */
  const get = useCallback(
    (stepIndex: number, step: DecryptedStep | null | undefined): ScratchSession | null => {
      if (!challengeId || !step?.has_sheet || !step.sheet || !step.question_id) return null;
      const existing = sessions.current.get(stepIndex);
      if (existing) return existing;
      if (broken.current.has(stepIndex)) return null;
      const questionId = step.question_id;
      // feuille v2 : on attend le moteur (sans feuille un instant) ; en panne, version du noyau
      const wantsV2 = hasPacksSheet(step.sheet) && mods !== 'failed';
      if (wantsV2 && (mods === null || mods === 'loading')) {
        if (mods === null) requestV2();
        return null;
      }
      const v2mods = wantsV2 && mods && typeof mods === 'object' ? mods : null;
      const data = parseScratchPayload(step.sheet, { allowPacks: !!v2mods });
      const entry = data.models.get(questionId);
      if (!entry) {
        broken.current.add(stepIndex);
        return null;
      }
      const attemptKey = String(stepIndex);
      try {
        const packs = entry.track === 'packs' && v2mods ? v2mods.packs : null;
        const s = new ScratchSession({
          model: packs ? withChoiceValues(entry.model) : entry.model,
          maxHora: entry.maxHora,
          ...(packs ? { packs, level: entry.level } : {}),
          calculator: false,
          stepDelayMs: prefersReducedMotion() ? 60 : 300,
          onCommit: (line) => {
            void recorder.record(linePayload(attemptKey, questionId, line, entry.track));
          },
          onErase: (line) => {
            void recorder.erase(erasePayload(attemptKey, questionId, line));
          },
        });
        const stored = data.lines.get(questionId);
        if (stored?.length) s.replay(stored);
        sessions.current.set(stepIndex, s);
        return s;
      } catch (e) {
        console.warn('[live-scratch] feuille ignorée', stepIndex, e);
        broken.current.add(stepIndex);
        return null;
      }
    },
    [challengeId, recorder, mods, requestV2],
  );

  /**
   * Interface v2 d'une feuille v2 : chaque ligne gardée est rejouée par la session de la
   * question (`acceptText`), qui l'enregistre et compte ses HORA. null : pas une feuille v2.
   */
  const getV2 = useCallback(
    (stepIndex: number, step: DecryptedStep | null | undefined): LiveV2Sheet | null => {
      if (!mods || typeof mods !== 'object') return null;
      if (!step?.sheet || !hasPacksSheet(step.sheet) || !step.question_id) return null;
      const base = get(stepIndex, step);
      if (!base) return null;
      let v = v2Sessions.current.get(stepIndex);
      if (!v) {
        const data = parseScratchPayload(step.sheet, { allowPacks: true });
        const entry = data.models.get(step.question_id);
        if (!entry || entry.track !== 'packs') return null;
        try {
          v = new mods.ui.V2Session({
            model: entry.model,
            moreKeys: !!entry.moreKeys,
            statement: { promptMd: step.prompt_md ?? '', level: entry.level },
            onCommit: (line) =>
              base.acceptText(line.text, line.hinted, !!line.revealed, line.simplified),
          });
          const stored = data.lines.get(step.question_id);
          if (stored?.length) v.replay(stored);
          if (base.getSnapshot().locked) v.lock();
        } catch (e) {
          console.warn('[live-scratch] interface v2 ignorée', stepIndex, e);
          return null;
        }
        v2Sessions.current.set(stepIndex, v);
      }
      return { session: v, ui: mods.ui };
    },
    [mods, get],
  );

  /** Question répondue ou terminée : la feuille passe en relecture. */
  const lock = useCallback((stepIndex: number) => {
    sessions.current.get(stepIndex)?.lock();
    v2Sessions.current.get(stepIndex)?.lock();
  }, []);

  return useMemo(() => ({ get, getV2, lock }), [get, getV2, lock]);
}
